import {DriverTracking, humanizeTrackingCode} from "./driver-tracking";
import {ArrowPathIcon, TruckIcon} from "@heroicons/react/20/solid";
import {ReactNode} from "react";
import {AdminPanel} from "../../components/AdminPanel";
import {DescriptionItem, DescriptionList} from "../../components/DescriptionList";
import {OtsAsyncActionButton} from "../../components/OtsButton";
import {OtsDateTime} from "../../components/OtsDateTime";
import {StatusPill} from "../../components/StatusPill";
import {DriverTrackingMap} from "./DriverTrackingMap";

export {humanizeTrackingCode} from "./driver-tracking";
export type {DriverTracking} from "./driver-tracking";

export type DriverTrackingPanelProps = {
  tracking?: DriverTracking;
  /** Shown as the destination until tracking names one. */
  fallbackDestinationName?: string;
  trailerName?: string;
  loading?: boolean;
  fetching?: boolean;
  error?: unknown;
  refreshInterval?: number;
  onRefresh?: () => void;
  density?: "compact" | "default";
  /** Shows the driver, origin, destination and route on a map. On by default. */
  showMap?: boolean;
  /** Falls back to the host app's Google Maps key. */
  mapApiKey?: string;
};

const formatDuration = (durationMillis?: number): string | undefined =>
  typeof durationMillis === "number" ? `${Math.round(durationMillis / 60000)} min` : undefined;

/** Live position, route and arrival estimate for the driver on a dispatch or dispatch request. */
export const DriverTrackingPanel = ({
  tracking,
  fallbackDestinationName,
  trailerName,
  loading,
  fetching,
  error,
  refreshInterval,
  onRefresh,
  density = "default",
  showMap = true,
  mapApiKey,
}: DriverTrackingPanelProps) => {
  const compact = density === "compact";
  const performer = tracking?.performer;
  const location = performer?.locationContext?.location;
  const origin = performer?.locationContext?.meta?.origin;
  const destination = tracking?.destination;
  const route = tracking?.route;
  const confidence = tracking?.confidence?.level;
  const status = tracking?.status ? humanizeTrackingCode(tracking.status) : performer ? "Assigned" : "Waiting";
  const speed = typeof location?.speed === "number" ? `${Math.round(location.speed * 2.23694)} mph` : undefined;
  const duration = formatDuration(route?.durationMillis);

  return (
    // container queries: the panel sits in tab panels and narrow GWT hosts as well as full-width pages
    <AdminPanel bodyClassName={compact ? "@container p-4" : "@container p-5 @2xl:p-7 @5xl:p-8"}>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2.5">
            <h2 className={["font-[family-name:var(--ots-font-display)] font-semibold text-[var(--ots-secondary)]", compact ? "text-base" : "text-lg"].join(" ")}>Driver tracking</h2>
            {!loading && <StatusPill tone={performer ? "info" : "neutral"}>{status}</StatusPill>}
          </div>
          <p className="mt-1.5 text-sm text-[var(--ots-text-muted)]">
            Live position and arrival estimate for the assigned driver.
            {confidence && <span> · {humanizeTrackingCode(confidence)} confidence</span>}
          </p>
        </div>
        {onRefresh && (
          <OtsAsyncActionButton
            onClick={onRefresh}
            isLoading={Boolean(fetching)}
            loadingLabel="Refreshing"
            variant="tonal"
            size="compact"
            icon={<ArrowPathIcon/>}>
            Refresh
          </OtsAsyncActionButton>
        )}
      </div>

      {loading && (
        <div className="mt-7 flex min-h-36 items-center justify-center rounded-md border border-[var(--ots-outline-variant)] bg-[var(--ots-background)] text-sm text-[var(--ots-text-muted)]">
          Loading driver tracking…
        </div>
      )}

      {!loading && error != null && (
        <div role="alert" className="mt-7 rounded-md border border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] px-5 py-6 text-sm text-[var(--ots-on-danger-container)]">
          Driver tracking could not be loaded. You can try refreshing it.
        </div>
      )}

      {!loading && error == null && !performer && (
        <div className="mt-7 rounded-md border border-dashed border-[var(--ots-outline)] bg-[var(--ots-background)] px-5 py-9 text-sm text-[var(--ots-text-muted)]">
          <p className="font-semibold text-[var(--ots-secondary)]">No active driver</p>
          <p className="mt-1.5">{tracking?.message ?? tracking?.confidence?.reason ?? "A driver location will appear after a job is assigned and tracking begins."}</p>
        </div>
      )}

      {!loading && error == null && performer && (
        <div className={["grid overflow-hidden rounded-md border border-[var(--ots-outline-variant)] @4xl:grid-cols-[minmax(0,1.25fr)_minmax(280px,0.75fr)]", compact ? "mt-4" : "mt-7"].join(" ")}>
          <div className={["bg-[var(--ots-background)]", compact ? "p-4" : "p-5 @2xl:p-7"].join(" ")}>
            <p className="text-xs font-medium text-[var(--ots-text-muted)]">Active route</p>
            <div className={["grid grid-cols-[auto_minmax(24px,1fr)_auto_minmax(24px,1fr)_auto] items-center", compact ? "mt-3" : "mt-6"].join(" ")}>
              <RouteNode label="Coming from" value={origin?.siteName ?? humanizeTrackingCode(origin?.source) ?? "Unknown"}/>
              <span className="h-px bg-[var(--ots-outline)]"/>
              <RouteNode label="Driver" value={performer.personName ?? "Assigned driver"} active icon={<TruckIcon className="size-4"/>}/>
              <span className="h-px bg-[var(--ots-outline)]"/>
              <RouteNode label="Destination" value={destination?.siteName ?? fallbackDestinationName ?? "Unknown"}/>
            </div>
            <div className={["flex flex-wrap gap-x-6 gap-y-2 border-t border-[var(--ots-outline-variant)] text-sm text-[var(--ots-text-muted)]", compact ? "mt-4 pt-3" : "mt-7 pt-5"].join(" ")}>
              {typeof route?.distanceMiles === "number" && <span><strong className="font-semibold text-[var(--ots-secondary)]">{route.distanceMiles.toFixed(1)} mi</strong> remaining</span>}
              {duration && <span><strong className="font-semibold text-[var(--ots-secondary)]">{duration}</strong> drive</span>}
              {route?.etaDttm && <span>ETA <strong className="font-semibold text-[var(--ots-secondary)]"><OtsDateTime value={route.etaDttm} /></strong></span>}
            </div>
          </div>

          <DescriptionList compact={compact} className={["!grid-cols-2 content-start border-t border-[var(--ots-outline-variant)] bg-[var(--ots-surface)] @4xl:border-t-0 @4xl:border-l", compact ? "p-4" : "gap-x-5 gap-y-6 p-5 @2xl:p-7"].join(" ")}>
            <DescriptionItem label="Driver / user">{performer.personName}</DescriptionItem>
            <DescriptionItem label="Trailer">{trailerName}</DescriptionItem>
            <DescriptionItem label="Company">{performer.personCompanyName}</DescriptionItem>
            <DescriptionItem label="Last location"><OtsDateTime value={performer.locationDttm ?? location?.dttm} /></DescriptionItem>
            <DescriptionItem label="Speed">{speed}</DescriptionItem>
            <DescriptionItem label="Refresh">{refreshInterval ? `${Math.round(refreshInterval / 1000)} seconds` : undefined}</DescriptionItem>
          </DescriptionList>
          {/* full width so the details column only takes the height it needs */}
          {showMap && <div className={["border-t border-[var(--ots-outline-variant)] @4xl:col-span-2", compact ? "p-4" : "p-5 @2xl:p-7"].join(" ")}>
            <DriverTrackingMap tracking={tracking} apiKey={mapApiKey}/>
          </div>}
        </div>
      )}
    </AdminPanel>
  );
};

const RouteNode = ({label, value, active, icon}: {label: string; value: string; active?: boolean; icon?: ReactNode}) => (
  <div className="flex max-w-36 flex-col items-center text-center">
    <span className={[
      "flex size-7 items-center justify-center rounded-full border",
      active
        ? "border-[var(--ots-primary)] bg-[var(--ots-primary)] text-[var(--ots-on-primary)]"
        : "border-[var(--ots-outline)] bg-[var(--ots-surface)] text-[var(--ots-text-muted)]"
    ].join(" ")}>
      {icon ?? <span className="size-2 rounded-full bg-current"/>}
    </span>
    <span className="mt-2 text-[10px] font-bold tracking-[0.05em] text-[var(--ots-text-muted)] uppercase">{label}</span>
    <span className="mt-1 line-clamp-2 text-xs font-semibold text-[var(--ots-secondary)]">{value}</span>
  </div>
);
